// src/components/shared/MacroTargets.jsx
// The one way we draw a set of macro targets: a card with the calorie pill, a
// proportion bar and a chip per macro, plus the editor that goes with it.
//
// Lifted out of AccountPanel's Nutrition Profile once the quiz results page
// needed the same thing — it had grown its own stat grid and table, so the two
// screens showed the same numbers in two unrelated designs.

import React, { useState } from 'react';
import { COLORS, FONTS } from 'styles/theme';
import RestrictionSearch from 'components/storefront/RestrictionSearch';
import { macroSplit, capViolations, isValidTarget, commitTargetEdit, CAPS_SUMMARY } from 'utils/macroTargets';

const MUTED = '#8a8d91';

// Macro colours, drawn from the site palette so the cards match the rest of
// the site rather than introducing a new chart palette.
const MACROS = [
  { key: 'p', label: 'Protein', field: 'proteinG', color: COLORS.dark },
  { key: 'c', label: 'Carbs', field: 'carbsG', color: COLORS.orange },
  { key: 'f', label: 'Fat', field: 'fatG', color: '#c2a878' },
];

// 16px, not 15: iOS zooms the page on focusing any input with smaller text
// and never zooms back (SFLANDING-142).
const FIELD = {
  width: '100%', boxSizing: 'border-box', padding: '10px 12px', fontSize: '16px',
  border: '1px solid #d8d0bd', borderRadius: '8px', background: '#fff',
  color: COLORS.dark, fontFamily: FONTS.sans,
};

const CHIP = {
  display: 'inline-flex', alignItems: 'center', gap: '6px',
  background: '#fff', border: `1px solid ${COLORS.creamAlt}`,
  borderRadius: '20px', padding: '4px 11px', fontSize: '13px', color: COLORS.dark,
};

/**
 * One set of targets — a meal's, or a whole day's.
 * @param {string} title heading for the card (meal name, "Daily total", …)
 * @param {object} targets {calories, proteinG, carbsG, fatG, fiberG}
 * @param {string} [note] explanatory text below the chips — e.g. why a meal
 *   marked "I skip it" still carries a nonzero calorie target. Sourced from
 *   the backend's meal_skip_notes (same computed text as the email), not
 *   reimplemented here, so the two surfaces can't drift.
 * @param {string[]} [restrictions] this meal's allergy/diet names; omitted hides the row
 * @param {React.ReactNode} [restrictionEditor] shown in place of the restriction chips
 * @param {React.ReactNode} [restrictionFooter] actions under the restriction chips
 * @param {React.ReactNode} [targetsEditor] shown in place of the macro chips, note and footer
 * @param {React.ReactNode} [footer] actions under the macro chips, e.g. an edit link
 */
export function MacroTargetCard({
  title, targets, note, restrictions, restrictionEditor, restrictionFooter, targetsEditor, footer,
}) {
  const split = macroSplit(targets);
  return (
    <div style={{
      background: COLORS.cream, border: `1px solid ${COLORS.creamAlt}`,
      borderRadius: '12px', padding: '16px', fontFamily: FONTS.sans,
    }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: '10px' }}>
        <span style={{ fontFamily: FONTS.serif, fontSize: '20px', fontWeight: 700, color: COLORS.dark }}>
          {title}
        </span>
        {!targetsEditor && (
          <span style={{
            background: COLORS.orange, color: '#fff', fontWeight: 700, fontSize: '13px',
            padding: '4px 12px', borderRadius: '20px', whiteSpace: 'nowrap',
          }}>
            {targets.calories ?? '—'} kcal
          </span>
        )}
      </div>

      {targetsEditor && <div style={{ marginTop: '12px' }}>{targetsEditor}</div>}

      {/* Proportion of the calories coming from each macro — the numbers alone
          made every meal look identical. */}
      {!targetsEditor && split && (
        <div style={{
          display: 'flex', height: '7px', borderRadius: '4px', overflow: 'hidden',
          background: COLORS.creamAlt, margin: '12px 0 10px',
        }}>
          {MACROS.map(({ key, color }) => (
            split[key] > 0
              ? <div key={key} style={{ width: `${split[key]}%`, background: color }} />
              : null
          ))}
        </div>
      )}

      {!targetsEditor && (
      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
        {MACROS.map(({ key, label, color, field }) => (
          <span key={key} style={CHIP}>
            <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: color }} />
            {label} <strong>{targets[field] ?? '—'}g</strong>
          </span>
        ))}
        <span style={{ ...CHIP, color: MUTED }}>
          Fiber <strong style={{ color: COLORS.dark, marginLeft: '4px' }}>{targets.fiberG ?? '—'}g</strong>
        </span>
      </div>
      )}

      {!targetsEditor && note && (
        <p style={{ fontSize: '13px', color: MUTED, lineHeight: 1.5, margin: '10px 0 0' }}>
          {note}
        </p>
      )}

      {!targetsEditor && footer}

      {(restrictions || restrictionEditor) && (
        <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: `1px solid ${COLORS.creamAlt}` }}>
          <div style={{ fontSize: '11px', letterSpacing: '1px', fontWeight: 700, color: COLORS.dark, marginBottom: '8px' }}>
            ALLERGIES &amp; RESTRICTIONS
          </div>
          {restrictionEditor || (restrictions.length ? (
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {restrictions.map((name) => (
                <span key={name} style={{ ...CHIP, fontWeight: 700 }}>{name}</span>
              ))}
            </div>
          ) : (
            <span style={{ fontSize: '13px', color: MUTED }}>None recorded</span>
          ))}
          {!restrictionEditor && restrictionFooter}
        </div>
      )}
    </div>
  );
}

/**
 * Picker for one meal's allergies, drawn inside its MacroTargetCard.
 * @param {object} draft {ids, labels, allMeals}
 * @param {boolean} [showAllMeals] offer "Apply to all my meals"
 */
export function MealRestrictionEditor({
  meal, draft, onChange, onSave, onCancel, saving = false, error = '', showAllMeals = false,
}) {
  return (
    <div>
      <RestrictionSearch
        selected={draft.ids}
        labelMap={draft.labels}
        onChange={(ids, labels) => onChange({ ...draft, ids, labels })}
      />
      {showAllMeals && (
        <label style={{ display: 'flex', gap: '8px', alignItems: 'center', fontSize: '13px', color: COLORS.dark, marginTop: '10px' }}>
          <input
            type="checkbox"
            checked={!!draft.allMeals}
            onChange={(e) => onChange({ ...draft, allMeals: e.target.checked })}
          />
          Apply to all my meals
        </label>
      )}
      <p style={{ fontSize: '12px', color: MUTED, margin: '10px 0 0', lineHeight: 1.6 }}>
        {draft.allMeals ? 'These apply to every meal we plan for you.' : `These apply to your ${meal.toLowerCase()} meals.`}
        {' '}Saving updates this week's plan onwards — meals already in production won't change.
      </p>
      {error && <p style={{ color: '#c0392b', fontSize: '13px', margin: '8px 0 0' }} role="alert">{error}</p>}
      <EditorActions saving={saving} onSave={onSave} onCancel={onCancel} saveLabel="Save Restrictions" />
    </div>
  );
}

export function EditorActions({ saving, disabled = false, onSave, onCancel, saveLabel }) {
  return (
    <div style={{ display: 'flex', gap: '10px', marginTop: '14px' }}>
      <button
        type="button"
        disabled={saving || disabled}
        onClick={onSave}
        style={{
          padding: '12px 20px', border: 'none', borderRadius: '8px', background: COLORS.orange,
          color: '#fff', fontWeight: 700, fontSize: '15px', fontFamily: FONTS.sans,
          cursor: saving ? 'default' : 'pointer', opacity: saving || disabled ? 0.5 : 1,
        }}
      >
        {saving ? 'Saving…' : saveLabel}
      </button>
      <button
        type="button"
        disabled={saving}
        onClick={onCancel}
        style={{
          padding: '12px 20px', borderRadius: '8px', border: `1.5px solid ${COLORS.creamAlt}`,
          background: '#fff', color: COLORS.dark, fontWeight: 700, fontSize: '15px',
          cursor: 'pointer', fontFamily: FONTS.sans,
        }}
      >
        Cancel
      </button>
    </div>
  );
}

/** The underlined link style the cards use for their own actions. */
export const cardLinkStyle = {
  marginTop: '12px', background: 'none', border: 'none', padding: 0,
  color: COLORS.text, fontSize: '13px', fontWeight: 700,
  textDecoration: 'underline', cursor: 'pointer', fontFamily: FONTS.sans,
};

/**
 * A number field that only reports a value when the customer leaves it.
 *
 * The editors used to reconcile the whole target set on every keystroke, and
 * a controlled input whose value is rewritten mid-typing can't be typed into:
 * "5" on the way to "500" became a 5-kcal day, every macro rounded to zero,
 * and the field showed 0 before the next digit landed (SFLANDING-95). So the
 * text lives here while it's being edited and is handed over once, on blur or
 * Enter, as the raw string.
 * @param {function} onCommit receives the raw string (may be '')
 */
export function MacroField({ label, value, onCommit, className, style }) {
  const [text, setText] = useState(null); // null = not currently being edited
  return (
    <label style={{ display: 'block' }}>
      {label && (
        <span style={{ fontSize: '12px', color: MUTED, display: 'block', marginBottom: '4px' }}>
          {label}
        </span>
      )}
      <input
        type="number"
        min="0"
        inputMode="decimal"
        className={className}
        value={text ?? (value ?? '')}
        onFocus={(e) => e.target.select()}
        onChange={(e) => setText(e.target.value)}
        onBlur={() => {
          if (text !== null) onCommit(text);
          setText(null);
        }}
        onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
        style={style}
      />
    </label>
  );
}

/**
 * Editor for one meal's targets. Calories are derived, never typed
 * independently — editing a macro recomputes them, editing calories rescales
 * the macros — and the caps block saving rather than silently clamping.
 * @param {string} meal which meal's caps apply
 * @param {object} draft current values
 * @param {function} onChange called with the reconciled draft
 * @param {string} [error] server-side failure to show alongside cap violations
 * @param {boolean} [embedded] drawn inside a MacroTargetCard, so no box or title of its own
 * Snack drafts also carry `snacksPerDay`; the macro targets are per snack.
 */
export function MacroTargetEditor({
  title, meal, draft, onChange, onSave, onCancel, saving = false, error = '', saveLabel = 'Save targets', embedded = false,
}) {
  const isSnack = meal === 'Snack' || meal === 'Snacks';
  const count = draft.snacksPerDay;
  const countError = isSnack && count != null && !(Number.isInteger(count) && count >= 1 && count <= 10)
    ? 'Snacks per day must be a whole number from 1 to 10.'
    : '';
  const violations = [...capViolations(meal, draft), ...(countError ? [countError] : [])];
  const valid = isValidTarget(meal, draft) && !countError;
  const commit = (key) => (raw) => onChange(commitTargetEdit(draft, key, raw));

  return (
    <div style={embedded ? { fontFamily: FONTS.sans } : {
      padding: '16px', borderRadius: '12px',
      background: '#fff', border: `1px solid ${COLORS.creamAlt}`, fontFamily: FONTS.sans,
    }}>
      {!embedded && (
        <div style={{ fontFamily: FONTS.serif, fontSize: '19px', fontWeight: 700, color: COLORS.dark, marginBottom: '12px' }}>
          {title}
        </div>
      )}

      <div style={{ display: 'grid', gap: '10px' }}>
        {isSnack && (
          <MacroField
            label="Snacks per day"
            value={count}
            onCommit={(raw) => onChange({ ...draft, snacksPerDay: raw === '' ? null : Number(raw) })}
            style={FIELD}
          />
        )}
        {[
          ['proteinG', 'Protein (g)'],
          ['carbsG', 'Carbs (g)'],
          ['fatG', 'Fat (g)'],
          ['fiberG', 'Fiber (g)'],
          ['calories', 'Calories (kcal)'],
        ].map(([key, label]) => (
          <MacroField
            key={key}
            label={label}
            value={draft[key]}
            onCommit={commit(key)}
            style={FIELD}
          />
        ))}
      </div>

      <p style={{ fontSize: '12px', color: MUTED, margin: '10px 0 0', lineHeight: 1.6 }}>
        {isSnack && 'Targets below are per snack. '}
        Calories update automatically from protein, carbs and fat. {CAPS_SUMMARY}
      </p>

      {violations.map((v) => (
        <p key={v} style={{ color: '#c0392b', fontSize: '13px', margin: '8px 0 0' }} role="alert">{v}</p>
      ))}
      {error && <p style={{ color: '#c0392b', fontSize: '13px', margin: '8px 0 0' }} role="alert">{error}</p>}

      <EditorActions saving={saving} disabled={!valid} onSave={onSave} onCancel={onCancel} saveLabel={saveLabel} />
    </div>
  );
}
